import React, { useState, useContext, useEffect } from 'react';
import Employee from '../types/api/Employee';
import { Box, Button, Checkbox, Chip, Dialog, Divider, FormControlLabel, FormGroup, Grid, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import EmployeeAvailabilities from '../components/Inputs/EmployeeAvailabilities';
import { AppContext } from '../context/appContext';
import WeeklyEmployeeAvailabilities from '../components/Inputs/WeeklyEmployeeAvailabilities';
import AssignFormationsPopup from '../components/Popups/AssignFormations';
import { listFormations } from '../api/restaurant';
import Formation from '../types/api/Formation';
import EditIcon from '@mui/icons-material/Edit';
import { EmployeeFormation } from '../types/api/EmployeeFormation';
import { ContractType } from '../types/api/Contract';

interface EmployeeFormProps {
    close: ()=>void,
    save: (employee: Employee)=>void,
    baseEmployee: Employee,
    confirmLabel: string,
    headerLabel: string,
}
export function EmployeeForm(EmployeeFormProps: EmployeeFormProps) {
  const appContext = useContext(AppContext);
  const [formation, setFormations] = useState<Formation[]>([]);
  const [employee, setEmployee] = useState<Employee>(EmployeeFormProps.baseEmployee)
  const [selectedContractType, setSelectedContractType] = useState<ContractType | "">("")
  const [addFormationsToEmployeePopup, setAddFormationsToEmployeePopup] = useState(false);

  useEffect(() => {
    const baseEmployee = EmployeeFormProps.baseEmployee;
    setEmployee(baseEmployee);

    const baseContracts = baseEmployee.contrats ?? [];
    const baseLatestContract = baseContracts.length > 0 ? baseContracts[baseContracts.length - 1] : null;
    const baseLatestContractEndDate = baseLatestContract?.endDate ? new Date(baseLatestContract.endDate as any) : null;
    const baseToday = new Date();
    baseToday.setHours(0, 0, 0, 0);
    if (baseLatestContractEndDate) {
      baseLatestContractEndDate.setHours(0, 0, 0, 0);
    }

    const baseLatestContractIsActive = !!baseLatestContract && (!baseLatestContractEndDate || baseLatestContractEndDate.getTime() > baseToday.getTime());
    setSelectedContractType(baseLatestContractIsActive && baseLatestContract ? baseLatestContract.contratType : "");
  }, [EmployeeFormProps.baseEmployee]);
  
  useEffect(() => {
    const updateFormations = async () => {
      const res = await listFormations();
      if (res && res.code === 200 && res.data) {
        setFormations(res.data);
      }
    };
    updateFormations();
  }, []);

  const isNumber = (value: any): boolean => {
      if(isNaN(value) || value < 0) {
        appContext.showSnackbar("Veuillez saisir un nombre d'heure valide ", 'error')
        return false
      }else {
        return true
      }
  }

  const isLastName = (value: any): boolean => {
    if(value === "") {
      appContext.showSnackbar("Veuillez saisir un nom", 'error')
      return false
    }else {
      return true
    }
  }

  const isFirstName = (value: any): boolean => {
    if(value === "") {
      appContext.showSnackbar("Veuillez saisir un prénom", 'error')
      return false
    }else {
      return true
  }
  }

  const isDate = (value: Date): boolean => {
    if( !isNaN(value.getTime())) {
      return true
    }
    else {
      appContext.showSnackbar("Veuillez saisir une date valide", 'error')
      return false
    }
  }

  const isDateCDDSet = (value: Date | string | null | undefined): boolean => {
    const date = value instanceof Date ? value : new Date(value ?? '');
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (!isNaN(date.getTime()) && date.getTime() >= today.getTime()) {
      return true
    }
    else {
      appContext.showSnackbar("Veuillez saisir une date de fin de contrat valide pour un CDD", 'error')
      return false
    }
  }

  const contracts = employee.contrats ?? [];
  const latestContract = contracts.length > 0 ? contracts[contracts.length - 1] : null;
  const latestContractEndDate = latestContract?.endDate ? new Date(latestContract.endDate as any) : null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (latestContractEndDate) {
    latestContractEndDate.setHours(0, 0, 0, 0);
  }
  const latestContractIsActive = !!latestContract && (!latestContractEndDate || latestContractEndDate.getTime() > today.getTime());
  const currentContract = latestContractIsActive ? latestContract : null;

  const openAssignFormationPopup = () => {
    setAddFormationsToEmployeePopup(true);
  };

    const closeAssignFormationsPopup = () => {
    setAddFormationsToEmployeePopup(false);
  };

  const saveAssignedFormationsToEmployee = (employeeId: number, formations: EmployeeFormation[]) => {
    const newFormations = formations.map(f => ({ ...f }));
    setEmployee(prev => {
      const updated = { ...prev, formations: newFormations } as Employee;
      return updated;
    });
    
    setAddFormationsToEmployeePopup(false);
  }

  return (
    <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, borderRadius: 2, }}>
      <Stack spacing={2}>
        {/* Header */}
        <Typography variant="h5" fontWeight={700}>
          {EmployeeFormProps.headerLabel}
        </Typography>

        {/* Row 1: Nom, Prénom, Taux horaire */}
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Nom"
              value={employee.lastName}
              onChange={(e) => setEmployee({ ...employee, lastName: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Prénom"
              value={employee.firstName}
              onChange={(e) => setEmployee({ ...employee, firstName: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Numéro de téléphone"
              value={employee.phone ?? ''}
              onChange={(e) => setEmployee({ ...employee, phone: e.target.value })}
            />
          </Grid> 
        </Grid>

        {/* Row 2: Heures mois, Heures semaine, Date d'entrée */}
        <Grid container spacing={2}>  
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="date"
              label="Date d'entrée"
              value={employee.entryDate.toISOString().split('T')[0]}
              onChange={(e) => {
                const date = new Date(e.target.value);
                if (!isNaN(date.getTime())) {
                  setEmployee({ ...employee, entryDate: date });
                }
              }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>

        <Divider />
        {/* Row 3: Téléphone, Type de contrat, Date de fin (si CDD) */}
        <Typography sx={{ color: "text.secondary", fontStyle: 'italic' }} variant="body2">
          Tout ce qui est modifé dans cette section créera un nouveau contrat 
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="number"
              label="Heures de contrat (mois)"
              error={employee.hoursPerMonth < 0}
              value={employee.hoursPerMonth ?? ''}
              onChange={(e) => setEmployee({ ...employee, hoursPerMonth: parseInt(e.target.value) })}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Heures / semaine"
              value={Math.round(employee.hoursPerMonth / 4.33)}
              InputProps={{ readOnly: true }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="number"
              inputProps={{ step: "0.01" }}
              label="Taux horaire (€ / h)"
              error={typeof employee.hourlyRate === 'number' && employee.hourlyRate < 0}
              value={employee.hourlyRate ?? ''}
              onChange={(e) => setEmployee({ ...employee, hourlyRate: parseFloat(e.target.value) })}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              select
              label="Type de contrat"
              value={selectedContractType}
              onChange={(event) => {
                const type = event.target.value as ContractType;
                
                setSelectedContractType(type);

                // Si on change le type ou s'il n'y a plus de contrat actif, créer un nouveau contrat
                if (currentContract && currentContract.contratType !== type) {
                  setEmployee({
                    ...employee,
                    contrats: [
                      ...(employee.contrats ?? []).map(c => {
                        if (c.id === currentContract.id && !c.endDate) {
                          return { ...c, endDate: new Date() };
                        }
                        return c;
                      }),
                      {
                        employeeId: employee.id,
                        contratType: type,
                        startDate: new Date(),
                        endDate: type === ContractType.CDI ? null : undefined,
                        createdAt: new Date(),
                      } as any
                    ]
                  });
                } else if (!currentContract) {
                  setEmployee({
                    ...employee,
                    contrats: [{
                      employeeId: employee.id,
                      contratType: type,
                      startDate: employee.entryDate,
                      endDate: null,
                      createdAt: new Date(),
                    } as any]
                  });
                }
              }}
            >
              {Object.values(ContractType).map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          {selectedContractType === ContractType.CDD && latestContract && (
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="date"
                label="Date de fin du contrat"
                value={
                  latestContract.endDate 
                    ? new Date(latestContract.endDate!).toISOString().split('T')[0] 
                    : ''
                }
                onChange={(e) => {
                  const date = new Date(e.target.value);
                  if (!isNaN(date.getTime())) {
                    const updatedContracts = [...employee.contrats!];
                    updatedContracts[updatedContracts.length - 1] = {
                      ...updatedContracts[updatedContracts.length - 1],
                      endDate: date
                    };
                    setEmployee({
                      ...employee,
                      contrats: updatedContracts
                    });
                  }
                }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          )}
        </Grid>

  
        <Divider />

        {/* Checkboxes row: Étudiant étranger */}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} >
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={employee.isForeignStudent}
                  onChange={() => setEmployee({ ...employee, isForeignStudent: !employee.isForeignStudent })}
                />
              }
              label="Étudiant étranger"
              labelPlacement="end"
            />
          </FormGroup>

          {employee.isForeignStudent && (
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
              <TextField
                type="number"
                label="Quota annuel"
                error={employee.foreignYearlyQuota < 0 || !employee.foreignYearlyQuota}
                value={employee.foreignYearlyQuota ?? ''}
                onChange={(e) => setEmployee({ ...employee, foreignYearlyQuota: parseInt(e.target.value) })}
              />
              <TextField
                type="date"
                label="Expiration du visa"
                value={employee.visaExpirationDate ? employee.visaExpirationDate.toISOString().split('T')[0] : new Date().toISOString().split('T')[0]}
                onChange={(e) => {
                  const date = new Date(e.target.value);
                  if (!isNaN(date.getTime())) {
                    setEmployee({ ...employee, visaExpirationDate: date });
                  }
                }}
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
          )}
        </Stack>

        {/* Checkbox: Temps plein */}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} >
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={employee.isFullTime}
                  onChange={() => setEmployee({ ...employee, isFullTime: !employee.isFullTime })}
                />
              }
              label="Temps plein"
              labelPlacement="end"
            />
          </FormGroup>
        </Stack>

        {/* Checkbox: Horaires par semaine */}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} >
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={employee.hasWeeklyAvailabilities}
                  onChange={() => setEmployee({ ...employee, hasWeeklyAvailabilities: !employee.hasWeeklyAvailabilities })}
                />
              }
              label="Horaires par semaine"
              labelPlacement="end"
            />
          </FormGroup>
        </Stack>

        <Divider />

        {/* Disponibilités régulières */}
        {!employee.hasWeeklyAvailabilities && (
          <Stack spacing={2}>
            <Typography variant="h6" textAlign="center" fontWeight={600}>
              Disponibilités régulières
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center', overflowX: 'auto' }}>
              <EmployeeAvailabilities
                readOnly={false}
                compact={false}
                baseEmployee={{ ...employee }}
                onUpdate={setEmployee}
              />
            </Box>
          </Stack>
        )}

        {/* Disponibilités hebdomadaires */}
        {employee.hasWeeklyAvailabilities && (
          <Stack spacing={2}>
            <Typography variant="h6" textAlign="center" fontWeight={600}>
              Disponibilités hebdomadaires
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center', overflowX: 'auto' }}>
              <WeeklyEmployeeAvailabilities
                readOnly={false}
                compact={false}
                employee={EmployeeFormProps.baseEmployee}
              />
            </Box>
          </Stack>
        )}

        <Divider />

        {/* Formations assignées */}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center" >
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Formations assignées :
            </Typography>
            {employee.formations.map((f) => (
              <Chip key={f.id} label={f.formation.label} />
            ))}
          </Box>
          <Button
            variant="contained"
            color="warning"
            startIcon={<EditIcon />}
            onClick={openAssignFormationPopup}
          >
            Modifier
          </Button>
        </Stack>

        {/* Action buttons */}
        <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ pt: 2 }}>
          <Button variant="contained" color="error" onClick={EmployeeFormProps.close}>
            Annuler
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={() => {
              const contracts = employee.contrats ?? [];
              const latestContract = contracts.length > 0 ? contracts[contracts.length - 1] : null;

              if (
                isLastName(employee.lastName) &&
                isFirstName(employee.firstName) &&
                isNumber(employee.hoursPerMonth) &&
                isNumber(employee.foreignYearlyQuota) &&
                (employee.visaExpirationDate ? isDate(employee.visaExpirationDate) : true) &&
                isNumber(employee.hourlyRate) &&
                isNumber(employee.hoursPerWeek) &&
                (selectedContractType !== ContractType.CDD || isDateCDDSet(latestContract?.endDate)) 
              ) {
                EmployeeFormProps.save(employee);
              }
            }}
          >
            {EmployeeFormProps.confirmLabel}
          </Button>
        </Stack>
      </Stack>

      {/* Dialog for assigning formations */}
      <Dialog fullWidth open={addFormationsToEmployeePopup} maxWidth="md">
        <AssignFormationsPopup
          formations={formation}
          employee={employee}
          closePopup={closeAssignFormationsPopup}
          assignFormationsToEmployee={saveAssignedFormationsToEmployee}
        />
      </Dialog>
    </Paper>
  );
}

export default EmployeeForm;
